import { faImage } from '@fortawesome/pro-light-svg-icons/faImage'
import { faSquareCaretRight } from '@fortawesome/pro-light-svg-icons/faSquareCaretRight'
import { TagColor } from '@space-core/components/customCard/base/enum'
import type { CardTag } from '@space-core/components/customCard/base/types'
import type { BundleCard } from '@space-core/components/customCard/bundle/types'
import type { ProductCard } from '@space-core/components/customCard/product/types'
import type { ProgressCard as ProgressCardProps } from '@space-core/components/customCard/progress/types'
import ProductDefaultImage from '@space-shell/assets/images/productDefaultImage.svg'
import type {
  Product as ProductBundle,
  ContentBundle
} from '@space-shell/common/services/bundle/types'
import { MediaType, PageType } from '@space-shell/common/services/product'
import type { Product } from '@space-shell/common/services/product'
import type { Progress, PurchasedData } from '@space-shell/common/services/product/types'

import type { SectionItem } from '@space-shell/common/services/sections'
import type { TFunction } from 'react-i18next'

import { DEFAULT_ICON, Icons, PREVIEW_PERCENTAGE, TagColors, Tags } from './constants'
import type { IconKeysType } from './types'

import { TimeUtils } from '../time'

export class ProductUtils {
  private static hasManuallyUploadedThumbnail({ pageType, mediaType, thumbnail }: Progress) {
    return pageType === PageType.CONTENT && !mediaType && thumbnail
  }

  private static getIcon(type?: IconKeysType, progress: Progress = {} as Progress) {
    const { mediaType, thumbnail } = progress

    if (mediaType === MediaType.VIDEO && !thumbnail) {
      return faSquareCaretRight
    }

    if (this.hasManuallyUploadedThumbnail(progress)) {
      return
    }

    return type ? Icons[type] : DEFAULT_ICON
  }

  private static getTag(t: TFunction, progress: Progress): CardTag | undefined {
    const { pageType, mediaType, mediaDuration } = progress

    if (!pageType) return pageType

    if (mediaType === MediaType.AUDIO) {
      return {
        text: TimeUtils.formatToMinuteAndSecond(mediaDuration),
        color: TagColor.GRAY
      }
    }

    if (this.hasManuallyUploadedThumbnail(progress)) {
      return
    }

    const tag = Tags[pageType]

    return { ...tag, text: t(tag.text) }
  }

  static progressMapping(progress: Progress, t: TFunction, isPreview: boolean): ProgressCardProps {
    const icon = this.getIcon(progress.mediaType || progress.pageType, progress)
    const tag = this.getTag(t, progress)
    const isVideo = progress.mediaType === MediaType.VIDEO
    const hasThumbnailAndIsVideo = progress.thumbnail && isVideo

    if (isPreview) {
      return {
        id: progress.id,
        name: t('preview.sections.my_progress.empty_state.title'),
        description: t('preview.sections.my_progress.empty_state.description'),
        completedPercentage: PREVIEW_PERCENTAGE,
        pageName: '',
        icon: faImage,
        iconSize: 'lg'
      }
    }

    return {
      id: progress.id,
      name: progress.name,
      description: progress.description,
      completedPercentage: progress.completedPercentage,
      pageName: progress.pageName,
      coverImage: progress.thumbnail,
      iconRounded: hasThumbnailAndIsVideo ? true : false,
      iconSize: hasThumbnailAndIsVideo ? 'sm' : 'md',
      ...(!isVideo ? { icon, tag } : { icon })
    }
  }

  static convertToProductCard(products: Product[], t: TFunction): ProductCard[] {
    return products.map((product) => {
      return {
        id: product.id,
        name: product.name,
        hotlink: product.hotlink,
        categoryId: product.categoryId,
        category: product.category ? t(`products.${product.category}`) : '',
        description: product.description ?? '',
        price: product.price ?? '',
        coverImage: product.coverPage ?? '',
        altText: product.altText ?? '',
        avatarAltText: product.avatarAltText ?? '',
        avatar: product.avatar ?? '',
        thumbnailImage: product.backgroundImageFinal ?? ProductDefaultImage,
        verticalCoverPage: product.verticalCoverPage ?? '',
        verticalAltText: product.verticalAltText ?? '',
        freeSignup: {
          isFreeSignup: product.freeSignup,
          text: t('tags.free_module'),
          color: TagColors.green
        }
      }
    })
  }

  static convertToBundleCard(bundle: ContentBundle[]): BundleCard[] {
    return bundle.map((bundle) => ({
      id: bundle.id,
      name: bundle.name,
      hotlink: bundle.hotlink,
      description: bundle.description ?? '',
      subscription: bundle.subscription,
      price: bundle.offerPrice?.offerFullPrice.label ?? '',
      thumbnailImage: bundle.backgroundImageFinal ?? ProductDefaultImage,
      avatar: bundle.avatar ?? '',
      showOfferPrice: bundle.showOfferPrice,
      verticalCoverPage: bundle.verticalCoverPage ?? '',
      verticalAltText: bundle.verticalAltText ?? '',
      altText: bundle.altText ?? '',
      avatarAltText: bundle?.avatarAltText ?? ''
    }))
  }

  static purchasedMapping(purchasedProduct: PurchasedData, t: TFunction): ProductCard {
    return {
      id: purchasedProduct.id,
      name: purchasedProduct.name,
      description: purchasedProduct.description,
      category: purchasedProduct.category ? t(`products.${purchasedProduct.category}`) : '',
      coverImage: purchasedProduct.coverPage ?? '',
      avatarAltText: purchasedProduct.avatarAltText ?? '',
      avatar: purchasedProduct.avatar ?? '',
      thumbnailImage: purchasedProduct.backgroundImageFinal ?? ProductDefaultImage,
      isPurchased: true,
      verticalCoverPage: purchasedProduct.verticalCoverPage ?? '',
      verticalAltText: purchasedProduct.verticalAltText ?? '',
      freeSignup: {
        isFreeSignup: !(purchasedProduct.purchased ?? true),
        text: t('tags.free_module'),
        color: TagColors.green
      }
    }
  }

  static bundleMapping(productBundle: ProductBundle, t: TFunction): ProductCard {
    return {
      id: productBundle.id,
      name: productBundle.name,
      description: productBundle.description,
      category: productBundle.category ? t(`products.${productBundle.category}`) : '',
      coverImage: productBundle.coverPage,
      thumbnailImage: productBundle.backgroundImageFinal ?? ProductDefaultImage,
      verticalCoverPage: productBundle.verticalCoverPage ?? '',
      verticalAltText: productBundle.verticalAltText ?? '',
      altText: productBundle.altText ?? ''
    }
  }

  static convertToSectionItem(products?: Product[]): SectionItem[] {
    if (!products) return []

    return products.map((product) => {
      return {
        title: product.name,
        description: product.description,
        itemId: String(product.id),
        image: {
          url: product.coverPage,
          altText: product.altText
        },
        avatarImage: {
          url: product.avatar,
          altText: product.avatarAltText
        },
        verticalImage: {
          url: product.verticalCoverPage,
          altText: product.verticalAltText
        },
        horizontalImage: {
          url: product.horizontalCoverPage,
          altText: product.horizontalAltText
        },
        bwImage: {
          url: product.backgroundImageFinal
        },
        category: product.category,
        communityId: product.communityId,
        categoryId: product.categoryId,
        price: product.price,
        productHotLink: product.hotlink || product.productHotLink || '',
        joined: product.joined || false,
        purchased: product.purchased || false,
        freeSignUp: product.freeSignup || false,
        accessLink: product.accessLink,
        affiliationCode: product.affiliationCode,
        clubSalesPage: product.clubSalesPage,
        version: product.version,
        checkoutInfo: product.checkoutInfo,
        couponInfo: product.couponInfo
      }
    })
  }
}
